import { z } from "zod";

import { env } from "./env.ts";
import { log } from "./log.ts";
import { officeReady } from "./office-ready.ts";

const handoffMessage = z.object({
  type: z.literal("bigpp:auth"),
  status: z.enum(["ok", "error"]),
  error: z.string().optional(),
});

export type AuthHandoffResult =
  | { ok: true }
  | { ok: false; error: string };

/**
 * Opens the dialog API pointed at the web app's add-in auth callback. The web
 * app, running on a real browser-trusted origin, performs the Better-Auth flow
 * (which sets its session cookie there) and then posts a result back via
 * `Office.context.ui.messageParent`. Cross-origin tRPC calls from the add-in
 * then carry that cookie (SameSite=None; Secure) automatically.
 */
export const openAuthDialog = async (): Promise<AuthHandoffResult> => {
  await officeReady;

  const url = `${env.VITE_WEB_URL}/auth/addin-callback?origin=${encodeURIComponent(env.VITE_ADDIN_URL)}`;

  return new Promise((resolve) => {
    Office.context.ui.displayDialogAsync(
      url,
      { height: 70, width: 32, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          resolve({ ok: false, error: asyncResult.error.message });
          return;
        }
        log.info("addin.auth.dialog.opened");
        const dialog = asyncResult.value;
        let messageReceived = false;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (event) => {
          messageReceived = true;
          // The Office runtime types this as a union; the `message` form has a string `message` field.
          if (!("message" in event)) {
            dialog.close();
            resolve({ ok: false, error: "Dialog reported an error before messaging" });
            return;
          }
          const parsed = safeParseHandoff(event.message);
          dialog.close();
          if (!parsed.ok) {
            log.info("addin.auth.dialog.received", { status: "error" });
            resolve({ ok: false, error: parsed.error });
            return;
          }
          log.info("addin.auth.dialog.received", { status: parsed.value.status });
          if (parsed.value.status === "ok") {
            resolve({ ok: true });
          } else {
            resolve({ ok: false, error: parsed.value.error ?? "Unknown auth error" });
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          if (!messageReceived) log.warn("addin.auth.dialog.closed_early");
          resolve({ ok: false, error: "Dialog closed before completing sign-in" });
        });
      },
    );
  });
};

const safeParseHandoff = (raw: string) => {
  try {
    const json: unknown = JSON.parse(raw);
    const result = handoffMessage.safeParse(json);
    if (!result.success) {
      return { ok: false as const, error: "Malformed auth message from dialog" };
    }
    return { ok: true as const, value: result.data };
  } catch {
    return { ok: false as const, error: "Auth message was not JSON" };
  }
};
